import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import QuoteHeader from './components/QuoteHeader';
import QuoteItemsTable from './components/QuoteItemsTable';
import QuoteSummary from './components/QuoteSummary';
import QuoteActions from './components/QuoteActions';
import DigitalSignature from './components/DigitalSignature';
import QuoteStatus from './components/QuoteStatus';

const DigitalQuoteReview = () => {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams?.get('token');
  
  const [quote, setQuote] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState('review'); // review, signature, completed
  const [quoteStatus, setQuoteStatus] = useState('pending');
  const [error, setError] = useState(null);

  // Mock quote data
  const mockQuote = {
    quoteNumber: "DEV-2025-001",
    createdDate: "07/01/2025",
    validUntil: "15/01/2025",
    clientCategory: "particulier",
    kitchenStyle: "moderne",
    client: {
      name: "Marie",
      surname: "Dubois",
      address: "15 Rue de la Paix, 75001 Paris",
      phone: "+33 1 42 86 17 23",
      email: "marie.dubois@email.com"
    },
    items: [
      {
        description: "Meubles hauts",
        details: "Style moderne, 8 unités",
        quantity: 8,
        unit: "unités",
        unitPrice: 450.00,
        total: 3600.00
      },
      {
        description: "Meubles bas",
        details: "Style moderne, 12 unités",
        quantity: 12,
        unit: "unités",
        unitPrice: 380.00,
        total: 4560.00
      },
      {
        description: "Colonnes",
        details: "Colonnes de rangement, 3 unités",
        quantity: 3,
        unit: "unités",
        unitPrice: 650.00,
        total: 1950.00
      },
      {
        description: "Angles hauts",
        details: "Meubles d'angle supérieurs, 2 unités",
        quantity: 2,
        unit: "unités",
        unitPrice: 520.00,
        total: 1040.00
      },
      {
        description: "Angles bas",
        details: "Meubles d'angle inférieurs, 2 unités",
        quantity: 2,
        unit: "unités",
        unitPrice: 480.00,
        total: 960.00
      },
      {
        description: "Plinthes",
        details: "Plinthes de finition",
        quantity: 15.5,
        unit: "ML",
        unitPrice: 25.00,
        total: 387.50
      },
      {
        description: "Éléments de finition",
        details: "Montants, cache-joints, joues latérales",
        quantity: 8,
        unit: "unités",
        unitPrice: 45.00,
        total: 360.00
      },
      {
        description: "Crédence",
        details: "Crédence carrelage",
        quantity: 8.2,
        unit: "ML",
        unitPrice: 85.00,
        total: 697.00
      },
      {
        description: "Plan de travail Compact",
        details: "Installation en U avec découpes",
        quantity: 1,
        unit: "ensemble",
        unitPrice: 1850.00,
        total: 1850.00
      },
      {
        description: "Découpes plan de travail",
        details: "Découpes pour évier et plaque de cuisson",
        quantity: 3,
        unit: "unités",
        unitPrice: 120.00,
        total: 360.00
      },
      {
        description: "Intégration plaque de cuisson",
        details: "Installation et raccordement",
        quantity: 1,
        unit: "unité",
        unitPrice: 180.00,
        total: 180.00
      },
      {
        description: "Intégration évier et robinetterie",
        details: "Installation complète",
        quantity: 1,
        unit: "ensemble",
        unitPrice: 320.00,
        total: 320.00
      },
      {
        description: "Intégration électroménager",
        details: "Four, micro-ondes, lave-vaisselle, hotte",
        quantity: 4,
        unit: "appareils",
        unitPrice: 150.00,
        total: 600.00
      },
      {
        description: "Modifications plomberie",
        details: "Adaptation des arrivées d\'eau",
        quantity: 1,
        unit: "forfait",
        unitPrice: 450.00,
        total: 450.00
      },
      {
        description: "Installation éclairage",
        details: "Éclairage sous-meuble LED",
        quantity: 1,
        unit: "ensemble",
        unitPrice: 280.00,
        total: 280.00
      },
      {
        description: "Modifications électriques",
        details: "Adaptation des prises et circuits",
        quantity: 1,
        unit: "forfait",
        unitPrice: 380.00,
        total: 380.00
      }
    ],
    summary: {
      subtotal: 17974.50,
      discount: 0,
      vatRate: 20,
      vatAmount: 3594.90,
      total: 21569.40,
      vatType: 'standard'
    }
  };

  useEffect(() => {
    // Simulate API call to fetch quote
    const fetchQuote = async () => {
      try {
        setIsLoading(true);
        
        // Validate token (mock validation)
        if (!token || token !== 'valid-token-123') {
          throw new Error('Token invalide ou expiré');
        }

        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setQuote(mockQuote);
        setQuoteStatus('pending');
      } catch (err) {
        setError(err?.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuote();
  }, [quoteId, token]);

  const handleApprove = () => {
    setCurrentStep('signature');
  };

  const handleReject = (rejectData) => {
    console.log('Quote rejected:', rejectData);
    setQuoteStatus('rejected');
    setCurrentStep('completed');
  };

  const handleSignature = (signatureData) => {
    console.log('Signature captured:', signatureData);
    setQuoteStatus('approved');
    setCurrentStep('completed');
  };

  const handleDownloadPDF = () => {
    console.log('Downloading PDF...');
    // Simulate PDF download
    const link = document.createElement('a');
    link.href = '#';
    link.download = `devis-${mockQuote?.quoteNumber}.pdf`;
    link?.click();
  };

  const handlePrint = () => {
    window.print();
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Helmet>
          <title>Erreur - Révision de Devis | KitchenQuote Pro</title>
        </Helmet>
        <div className="max-w-md w-full bg-surface border border-error/20 rounded-lg p-6 text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-error/10 rounded-full mx-auto mb-4">
            <Icon name="AlertTriangle" size={32} className="text-error" />
          </div>
          <h1 className="text-xl font-semibold text-text-primary mb-2">Accès Refusé</h1>
          <p className="text-text-secondary mb-4">{error}</p>
          <Button
            variant="outline"
            iconName="ArrowLeft"
            iconPosition="left"
            onClick={() => window.history?.back()}
          >
            Retour
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Révision de Devis - {quote?.quoteNumber || 'Chargement...'} | KitchenQuote Pro</title>
        <meta name="description" content="Examinez et approuvez votre devis de cuisine avec signature numérique sécurisée" />
      </Helmet>
      <div className="max-w-4xl mx-auto p-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-text-primary mb-2">
            Révision de Devis Numérique
          </h1>
          <p className="text-text-secondary">
            Examinez attentivement votre devis et prenez votre décision en toute sécurité
          </p>
        </div>

        {/* Progress Indicator */}
        {currentStep !== 'completed' && (
          <div className="flex items-center justify-center space-x-4 mb-8">
            <div className={`flex items-center space-x-2 ${
              currentStep === 'review' ? 'text-primary' : 'text-success'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep === 'review' ? 'bg-primary text-white' : 'bg-success text-white'
              }`}>
                {currentStep === 'review' ? '1' : <Icon name="Check" size={16} />}
              </div>
              <span className="text-sm font-medium">Révision</span>
            </div>
            
            <div className={`w-16 h-0.5 ${
              currentStep === 'signature' ? 'bg-primary' : 'bg-border'
            }`}></div>
            
            <div className={`flex items-center space-x-2 ${
              currentStep === 'signature' ? 'text-primary' : 'text-text-secondary'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep === 'signature' ? 'bg-primary text-white' : 'bg-muted text-text-secondary'
              }`}>
                2
              </div>
              <span className="text-sm font-medium">Signature</span>
            </div>
          </div>
        )}

        {/* Quote Status */}
        {currentStep === 'completed' && (
          <QuoteStatus 
            status={quoteStatus}
            onDownloadPDF={handleDownloadPDF}
            onPrint={handlePrint}
          />
        )}

        {/* Quote Content */}
        <QuoteHeader quote={quote} isLoading={isLoading} />
        <QuoteItemsTable items={quote?.items || []} isLoading={isLoading} />
        <QuoteSummary summary={quote?.summary} isLoading={isLoading} />

        {/* Actions based on current step */}
        {currentStep === 'review' && (
          <QuoteActions
            onApprove={handleApprove}
            onReject={handleReject}
            isLoading={isLoading}
            showSignature={false}
            onShowSignature={handleApprove}
          />
        )}

        {currentStep === 'signature' && (
          <DigitalSignature
            onSignature={handleSignature}
            clientName={`${quote?.client?.name} ${quote?.client?.surname}`}
            isLoading={isLoading}
          />
        )}

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-border text-center">
          <div className="flex items-center justify-center space-x-2 text-sm text-text-secondary mb-2">
            <Icon name="Shield" size={16} />
            <span>Connexion sécurisée SSL - Vos données sont protégées</span>
          </div>
          <p className="text-xs text-text-secondary">
            © {new Date()?.getFullYear()} KitchenQuote Pro. Tous droits réservés.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DigitalQuoteReview;